// 화면 그리기(§9.8) — 세션 화면에 편집기·`draw` 채널을 붙이는 훅.
//
// 🔴 **캔버스는 React 밖에서 다시 그린다.** 편집기는 마우스가 움직일 때마다 바뀌는데, 그때마다 React 상태를 바꾸면
//    세션 화면 전체(700줄 컴포넌트 — 영상·패널·도구막대)가 초당 수십 번 다시 그려진다.
//    ⇒ 편집기 변경은 `subscribe` 로 캔버스에만 알리고, React 는 **도구 막대에 보이는 값**(도구·색·굵기·되돌리기 가능 등)이
//       바뀔 때만 갱신한다(`uiKey` 비교).
// 채널이 없으면(구버전 에이전트·`RS_AGENT_DRAW=0`) 빈 링크로 **뷰어 단독** 그리기가 그대로 된다(PNG 저장용).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '../sessions/types';
import type { DrawEditor } from './editor.ts';
import { DrawSync } from './sync.ts';
import { REMOTE_OPEN_TIMEOUT_MS } from './protocol.ts';
import { canvasToPng, composeSnapshot, SNAPSHOT_MIME, SNAPSHOT_PREFIX } from './snapshot.ts';
import { recordingFileName } from '../recording/protocol';
import { saveFileToDisk, saveNoticeOf } from '../download';
import type { SaveNotice } from '../download';
import type { DrawLink } from './sync.ts';
import type { Author } from './model.ts';
import type { Tool } from './style.ts';
import { confirmAction } from '../ui/confirm';

/** 원격지 그림판 상태(도구 막대에 보인다). */
export type RemoteState =
  /** 상대에게 보이지 않는다 — 채널 없음 또는 원격지에 앱 없음. */
  | 'local-only'
  /** 켜기를 보냈고 원격지 그림판이 열리기를 기다린다. */
  | 'opening'
  /** 원격지 그림판이 열렸다(양쪽에 보인다). */
  | 'shared';

export interface Drawing {
  active: boolean;
  setActive: (on: boolean) => void;
  /** 사용자가 끈다 — 그린 것이 있으면 확인 받고. 껐으면 true. */
  requestOff: () => Promise<boolean>;
  editor: DrawEditor;
  tool: Tool;
  /** 마우스·키보드를 그리기가 가져가는가(켜져 있고 도구가 `조작` 이 아님) — 이때 원격으로 입력을 보내지 않는다. */
  capturesInput: boolean;
  subscribe: (fn: () => void) => () => void;
  remote: RemoteState;
  /** 원격지 사람이 지금 그리는 중. */
  peerBusy: boolean;
  /** 캔버스에서 그리지 않을 작성자(원격지 그림판이 캡처에서 빠지지 않는 기기 — 영상에 이미 찍혀 온다). */
  hideAuthor: Author | null;
  /** 한 번 보여 줄 알림(원격지가 끔 등). */
  notice: string | null;
  clearNotice: () => void;
  /** 화면 + 그림을 PNG 로 저장(원격 화면 원래 해상도 — `snapshot.ts`). */
  savePng: (video: HTMLVideoElement | null) => Promise<void>;
  /** 저장 결과 안내(성공도 알린다 — `SaveNoticeBar`). */
  saveNotice: SaveNotice | null;
  clearSaveNotice: () => void;
}

function uiKey(ed: DrawEditor): string {
  return [ed.tool, ed.color, ed.width, ed.fill, ed.doc.board, ed.canUndo, ed.canRedo, ed.selection.size, ed.doc.size > 0].join('|');
}

/** 채널이 없을 때의 링크 — 보내는 것은 버리고 받을 것은 없다. */
const NULL_LINK: DrawLink = { send: () => {}, setListener: () => {} };

/**
 * @param deviceLabel 저장 파일 이름에 넣을 기기 이름(녹화 파일과 같은 규칙 — `recordingFileName`)
 */
export function useDrawing(session: Session, deviceLabel: string): Drawing {
  const listeners = useRef(new Set<() => void>());
  const [, setUi] = useState('');
  const [active, setActiveState] = useState(false);
  const [ready, setReady] = useState<{ excluded: boolean } | null>(null);
  const [peerBusy, setPeerBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const activeRef = useRef(false);
  activeRef.current = active;

  const channel = session.client.draw;
  const sync = useMemo(() => {
    const s: DrawSync = new DrawSync(
      'viewer',
      channel ?? NULL_LINK,
      {
        onRemoteOff: () => {
          if (!activeRef.current) return;
          setActiveState(false);
          setReady(null);
          setPeerBusy(false);
          setNotice('상대가 그리기를 껐습니다 — 그린 것이 모두 지워졌습니다');
        },
        onRemoteReady: (excluded) => setReady({ excluded }),
        onPeerBusy: setPeerBusy,
      },
      () => {
        for (const fn of listeners.current) fn();
        setUi(uiKey(s.editor));
      },
    );
    return s;
  }, [channel]);

  // 채널이 바뀌거나 화면이 사라지면 떼어 낸다(이전 세션의 획이 새 화면으로 흘러오지 않게).
  useEffect(
    () => () => {
      sync.detach();
      sync.editor.reset();
    },
    [sync],
  );

  const remoteUi = !!channel && session.drawCaps?.ui === 'app';

  // 원격지 그림판이 시한 안에 답하지 않으면 도구 막대의 상태 칩이 "내 화면에만" 으로 내려간다(REMOTE_OPEN_TIMEOUT_MS 주석).
  const [openTimedOut, setOpenTimedOut] = useState(false);
  const waiting = active && remoteUi && !ready;
  useEffect(() => {
    setOpenTimedOut(false);
    if (!waiting) return;
    const t = setTimeout(() => setOpenTimedOut(true), REMOTE_OPEN_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [waiting]);

  const setActive = useCallback(
    (on: boolean) => {
      setActiveState(on);
      setNotice(null);
      if (on) {
        sync.editor.setTool('pen'); // 켜자마자 그릴 수 있게(조작으로 돌아가려면 Esc)
        sync.announceOn();
      } else {
        sync.announceOff(); // 끄면 양쪽 모두 사라진다(§9.8.0 — 세션 한정, 남기려면 PNG)
        setReady(null);
        setPeerBusy(false);
      }
    },
    [sync],
  );

  /**
   * 사용자가 그리기를 끈다 — 그린 것이 있으면 **확인 받고**(끄면 양쪽 그림이 모두 사라진다, 사용자 요청 2026-09-27). 껐으면 true.
   * 세션 막대 [그리기]·그리기 막대 ✕ 가 같이 쓴다.
   */
  const requestOff = useCallback(async (): Promise<boolean> => {
    if (sync.editor.doc.visible().length > 0) {
      const ok = await confirmAction({
        title: '그리기를 끌까요?',
        message: '양쪽 화면에 그린 그림이 모두 사라집니다. 남기려면 먼저 💾 PNG 로 저장하세요.',
        confirmLabel: '그리기 끄기',
        danger: true,
      });
      if (!ok) return false;
    }
    setActive(false);
    return true;
  }, [sync, setActive]);

  const subscribe = useCallback((fn: () => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  }, []);

  const hideAuthor: Author | null = ready && !ready.excluded ? 'r' : null;

  // PNG 저장 — 화면과 **같은 그리기 규칙**(원격지 그림이 영상에 이미 찍혀 오면 다시 그리지 않는다).
  const [saveNotice, setSaveNotice] = useState<SaveNotice | null>(null);
  const labelRef = useRef(deviceLabel);
  labelRef.current = deviceLabel;
  const hideRef = useRef(hideAuthor);
  hideRef.current = hideAuthor;
  const savePng = useCallback(
    async (video: HTMLVideoElement | null) => {
      try {
        const png = await canvasToPng(composeSnapshot(video, sync.editor.doc, hideRef.current));
        const name = recordingFileName(labelRef.current, new Date(), 'png', SNAPSHOT_PREFIX);
        setSaveNotice(saveNoticeOf('화면과 그림을', await saveFileToDisk(name, png, SNAPSHOT_MIME)));
      } catch (e) {
        setSaveNotice({ kind: 'error', text: `이미지를 만들지 못했습니다: ${String(e)}` });
      }
    },
    [sync],
  );

  const editor = sync.editor;
  return {
    active,
    setActive,
    requestOff,
    editor,
    tool: editor.tool,
    capturesInput: active && editor.tool !== 'control',
    subscribe,
    remote: !remoteUi || (openTimedOut && !ready) ? 'local-only' : ready ? 'shared' : 'opening',
    peerBusy,
    hideAuthor,
    notice,
    clearNotice: () => setNotice(null),
    savePng,
    saveNotice,
    clearSaveNotice: () => setSaveNotice(null),
  };
}
